import type { Colors, Point, SpriteSheetItem } from './types';

type CanvasImageSource =
  | HTMLCanvasElement
  | HTMLImageElement
  | SVGImageElement
  | HTMLVideoElement
  | ImageBitmap
  | OffscreenCanvas;

const SCALE = 0.2 * (1 / 80); // the reference sprite width should be 1/3rd the (half-)roadWidth

function rumbleWidth(projectedRoadWidth: number, div: number, lanes: number): number {
  return projectedRoadWidth / Math.max(div, 2 * lanes);
}
function laneMarkerWidth(projectedRoadWidth: number, lanes: number): number {
  return projectedRoadWidth / Math.max(32, 8 * lanes);
}

export function polygon(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.lineTo(x4, y4);
  ctx.closePath();
  ctx.fill();
}

export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius:
    | number
    | {
        topLeft: number;
        topRight: number;
        bottomLeft: number;
        bottomRight: number;
      },
  color: string,
): void {
  let { topLeft, topRight, bottomLeft, bottomRight } = {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  };

  if (typeof radius === 'number') {
    topLeft = radius;
    topRight = radius;
    bottomLeft = radius;
    bottomRight = radius;
  }

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x + topLeft, y);
  ctx.lineTo(x + width - topRight, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + topRight);
  ctx.lineTo(x + width, y + height - bottomRight);
  ctx.quadraticCurveTo(x + width, y + height, x + width - bottomRight, y + height);
  ctx.lineTo(x + bottomLeft, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - bottomLeft);
  ctx.lineTo(x, y + topLeft);
  ctx.quadraticCurveTo(x, y, x + topLeft, y);
  ctx.closePath();
  ctx.fill();
}

export function line(
  ctx: CanvasRenderingContext2D,
  points: Point[],
  lineWidth: number,
  color: string,
  dashPoint = 1,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;

  ctx.beginPath();

  const dashPointRatio = Math.floor(points.length * dashPoint);

  for (let i = 0; i < dashPointRatio; i++) {
    const p = points[i];
    ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();

  if (dashPointRatio !== points.length) {
    ctx.setLineDash([5, 5]);

    for (let i = dashPointRatio; i < points.length; i++) {
      const p = points[i];
      ctx.lineTo(p.x, p.y);
    }

    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.closePath();
}

export function circle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
  strokeWidth?: number,
  strokeColor?: string,
  opacity = 1,
): void {
  ctx.fillStyle = color;
  ctx.globalAlpha = opacity;
  ctx.moveTo(x, y);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.fill();
  if (strokeWidth) {
    if (strokeColor) ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

export function background(
  ctx: CanvasRenderingContext2D,
  background: CanvasImageSource,
  width: number,
  height: number,
  layer: SpriteSheetItem,
  rotation = 0,
  offset = 0,
): void {
  const imageW = layer.w / 2;
  const imageH = layer.h;

  const sourceX = layer.x + Math.floor(layer.w * rotation);
  const sourceY = layer.y;
  const sourceW = Math.min(imageW, layer.x + layer.w - sourceX);
  const sourceH = imageH;

  const destX = 0;
  const destY = offset;
  const destW = Math.floor(width * (sourceW / imageW));
  const destH = height;

  ctx.drawImage(background, sourceX, sourceY, sourceW, sourceH, destX, destY, destW, destH);

  if (sourceW < imageW)
    ctx.drawImage(background, layer.x, sourceY, imageW - sourceW, sourceH, destW - 1, destY, width - destW, destH);
}

export function sprite(
  ctx: CanvasRenderingContext2D,
  width: number,
  roadWidth: number,
  sprites: CanvasImageSource,
  sprite: SpriteSheetItem,
  scale: number,
  destX: number,
  destY: number,
  offsetX = 0,
  offsetY = 0,
  clipY: number | undefined = undefined,
): void {
  //  scale for projection AND relative to roadWidth (for tweakUI)
  const destW = ((sprite.w * scale * width) / 2) * (SCALE * roadWidth);
  const destH = ((sprite.h * scale * width) / 2) * (SCALE * roadWidth);

  const internalDestX = destX + destW * offsetX;
  const internalDestY = destY + destH * offsetY;

  const clipH = clipY ? Math.max(0, internalDestY + destH - clipY) : 0;

  if (clipH < destH) {
    ctx.drawImage(
      sprites,
      sprite.x,
      sprite.y,
      sprite.w,
      sprite.h - (sprite.h * clipH) / destH,
      internalDestX,
      internalDestY,
      destW,
      destH - clipH,
    );
  }
}

export function road(
  ctx: CanvasRenderingContext2D,
  width: number,
  x1: number,
  y1: number,
  w1: number,
  x2: number,
  y2: number,
  w2: number,
  colors: Colors['road'],
  lanes: number,
  drawLane: boolean,
): void {
  const r1 = rumbleWidth(w1, 6, lanes);
  const r2 = rumbleWidth(w2, 6, lanes);
  const r11 = rumbleWidth(w1, 3, lanes);
  const r21 = rumbleWidth(w1, 3, lanes);

  ctx.fillStyle = colors.grass;
  ctx.fillRect(0, y2, width, y1 - y2);

  polygon(ctx, x1 - w1 - r11, y1, x1 - w1, y1, x2 - w2, y2, x2 - w2 - r21, y2, colors.rumble);
  polygon(ctx, x1 - w1 - r1, y1, x1 - w1, y1, x2 - w2, y2, x2 - w2 - r2, y2, colors.rumbleShadow);
  polygon(ctx, x1 + w1 + r11, y1, x1 + w1, y1, x2 + w2, y2, x2 + w2 + r21, y2, colors.rumble);
  polygon(ctx, x1 + w1 + r1, y1, x1 + w1, y1, x2 + w2, y2, x2 + w2 + r2, y2, colors.rumbleShadow);
  polygon(ctx, x1 - w1, y1, x1 + w1, y1, x2 + w2, y2, x2 - w2, y2, colors.background);

  if (colors.lane && drawLane) {
    const l1 = laneMarkerWidth(w1, lanes);
    const l2 = laneMarkerWidth(w2, lanes);
    const laneW1 = (w1 * 2) / lanes;
    const laneW2 = (w2 * 2) / lanes;

    let laneX1 = x1 - w1 + laneW1;
    let laneX2 = x2 - w2 + laneW2;

    for (let lane = 1; lane < lanes; laneX1 += laneW1, laneX2 += laneW2, lane++) {
      polygon(ctx, laneX1 - l1 / 2, y1, laneX1 + l1 / 2, y1, laneX2 + l2 / 2, y2, laneX2 - l2 / 2, y2, colors.lane);
    }
  }
}
